import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Chip,
  Tooltip,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Divider,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import axiosInstance from '../utils/axios';
import { useNavigate } from 'react-router-dom';
import { AdminFilterBar, AdminPageHeader, useAdminFeedback } from '../components/admin/primitives';

const ROLE_FILTER_OPTIONS = [
  { label: 'All roles', value: '' },
  { label: 'Admin', value: 'admin' },
  { label: 'Event Team', value: 'eventteam' },
  { label: 'HR Team', value: 'hrteam' },
  { label: 'Finance Team', value: 'financeteam' },
  { label: 'Driver Head Manager', value: 'driverHeadManager' },
  { label: 'Community Manager', value: 'communityManager' },
  { label: 'RTO', value: 'rto' },
  { label: 'Rider', value: 'rider' },
];

const ROLE_ASSIGN_OPTIONS = ROLE_FILTER_OPTIONS.filter((o) => o.value);

function roleLabel(value) {
  return ROLE_ASSIGN_OPTIONS.find((o) => o.value === value)?.label || value || '—';
}

function canUpdateUserRole(user) {
  if (typeof user?.canUpdateRole === 'boolean') return user.canUpdateRole;
  return Boolean(user?.riderId || user?.linkedAccountId || user?.accountId);
}

export default function AdminUsers() {
  const navigate = useNavigate();
  const { showSuccess, showError, Feedback } = useAdminFeedback();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [role, setRole] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [updatingRoleId, setUpdatingRoleId] = useState(null);
  const [actionUser, setActionUser] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [confirm, setConfirm] = useState({ open: false, type: null, user: null });
  const [confirmBusy, setConfirmBusy] = useState(false);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);
  const visibleItems = useMemo(() => items.filter(canUpdateUserRole), [items]);
  const menuOpen = Boolean(menuAnchor);

  const fetchUsers = async (overrides = {}) => {
    const currentPage = overrides.page ?? page;
    const currentRole = overrides.role !== undefined ? overrides.role : role;
    const currentSearch = overrides.search !== undefined ? overrides.search : search;
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/users', {
        params: {
          page: currentPage,
          limit,
          role: currentRole || undefined,
          search: currentSearch || undefined,
        },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers().catch((e) => {
      showError(e?.response?.data?.message || 'Failed to load users');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  const applyFilters = () => {
    setPage(1);
    fetchUsers({ page: 1, role, search }).catch((e) => {
      showError(e?.response?.data?.message || 'Failed to load users');
    });
  };

  const handleReset = () => {
    setRole('');
    setSearch('');
    setPage(1);
    fetchUsers({ page: 1, role: '', search: '' }).catch((e) => {
      showError(e?.response?.data?.message || 'Failed to load users');
    });
  };

  const updateRole = async (id, newRole) => {
    const previousRole = items.find((u) => u._id === id)?.role;
    if (!newRole || newRole === previousRole) return;

    setUpdatingRoleId(id);
    setItems((prev) => prev.map((u) => (u._id === id ? { ...u, role: newRole } : u)));
    try {
      await axiosInstance.patch(`/users/${id}/role`, { role: newRole });
      showSuccess(`Role updated to ${roleLabel(newRole)}`);
    } catch (e) {
      setItems((prev) => prev.map((u) => (u._id === id ? { ...u, role: previousRole } : u)));
      showError(e?.response?.data?.message || 'Failed to update role');
    } finally {
      setUpdatingRoleId(null);
    }
  };

  const openRowMenu = (event, user) => {
    setMenuAnchor(event.currentTarget);
    setActionUser(user);
  };

  const closeRowMenu = () => {
    setMenuAnchor(null);
    setActionUser(null);
  };

  const openConfirm = (type, user) => {
    closeRowMenu();
    setConfirm({ open: true, type, user });
  };

  const closeConfirm = () => {
    if (confirmBusy) return;
    setConfirm({ open: false, type: null, user: null });
  };

  const runConfirmedAction = async () => {
    const { type, user } = confirm;
    if (!user?._id || !type) return;

    setConfirmBusy(true);
    try {
      if (type === 'resetPassword') {
        const { data } = await axiosInstance.post(`/users/${user._id}/reset-password-to-steam`);
        setItems((prev) =>
          prev.map((row) =>
            row._id === user._id
              ? { ...row, mustChangePassword: true, steamId: data?.user?.steamId || row.steamId }
              : row
          )
        );
        showSuccess(data?.message || 'Password reset to Steam ID');
      } else if (type === 'delete') {
        await axiosInstance.delete(`/users/${user._id}`);
        showSuccess('User deleted');
        await fetchUsers();
      }
      setConfirm({ open: false, type: null, user: null });
    } catch (e) {
      showError(
        e?.response?.data?.message ||
          (type === 'delete' ? 'Failed to delete user' : 'Failed to reset password')
      );
    } finally {
      setConfirmBusy(false);
    }
  };

  const confirmCopy = (() => {
    const user = confirm.user;
    if (!user) return { title: '', body: '', confirmLabel: 'Confirm', color: 'primary' };
    if (confirm.type === 'resetPassword') {
      return {
        title: 'Reset password to Steam ID?',
        body: `Set ${user.username}'s password to their Steam ID (${user.steamId}). They will be required to choose a new password on next login.`,
        confirmLabel: 'Reset password',
        color: 'warning',
      };
    }
    return {
      title: 'Delete user?',
      body: `Permanently delete ${user.username} (${user.email || 'no email'}). This cannot be undone.`,
      confirmLabel: 'Delete user',
      color: 'error',
    };
  })();

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: 3, py: 4 }}>
      <AdminPageHeader
        description="Search accounts, change roles, and manage access."
        actions={
          <Button
            variant="contained"
            startIcon={<PersonAddAlt1OutlinedIcon />}
            onClick={() => navigate('/admin/create-user')}
          >
            Create user
          </Button>
        }
      />

      <AdminFilterBar
        search={{
          value: search,
          onChange: setSearch,
          onSubmit: applyFilters,
          placeholder: 'Search username or name',
        }}
        onReset={handleReset}
      >
        <TextField
          select
          label="Role"
          size="small"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          sx={{ width: 200 }}
        >
          {ROLE_FILTER_OPTIONS.map((o) => (
            <MenuItem key={o.value || 'all'} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="contained" startIcon={<SearchOutlinedIcon />} onClick={applyFilters}>
          Search
        </Button>
      </AdminFilterBar>

      <Card variant="outlined">
        <CardContent sx={{ pb: 2, '&:last-child': { pb: 2 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            spacing={1.5}
            sx={{ mb: 1.5 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle1" fontWeight={600}>
                Users
              </Typography>
              <Chip size="small" label={`${total} total`} variant="outlined" />
              {loading && <CircularProgress size={16} />}
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
              <TextField
                select
                size="small"
                label="Rows"
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                sx={{ width: 100 }}
              >
                {[10, 20, 50, 100].map((n) => (
                  <MenuItem key={n} value={n}>
                    {n}
                  </MenuItem>
                ))}
              </TextField>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, p) => setPage(p)}
                shape="rounded"
                size="small"
              />
            </Stack>
          </Stack>

          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, width: 64 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleItems.map((u) => {
                  const hasSteamId = Boolean(u.steamId);
                  const roleBusy = updatingRoleId === u._id;

                  return (
                    <TableRow key={u._id || u.riderId} hover>
                      <TableCell sx={{ py: 1.5, maxWidth: 320 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {u.username || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                          {u.email || 'No email'}
                        </Typography>
                        {hasSteamId && (
                          <Typography
                            variant="caption"
                            color="text.disabled"
                            sx={{ fontFamily: 'ui-monospace, monospace' }}
                            noWrap
                            display="block"
                          >
                            Steam {u.steamId}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <TextField
                          select
                          size="small"
                          value={u.role || ''}
                          disabled={roleBusy || !u._id}
                          onChange={(e) => updateRole(u._id, e.target.value)}
                          fullWidth
                          sx={{
                            minWidth: 180,
                            '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' },
                          }}
                        >
                          {ROLE_ASSIGN_OPTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                              {o.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                          {u.mustChangePassword ? (
                            <Chip size="small" label="Password reset pending" color="warning" />
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              Active
                            </Typography>
                          )}
                          {!hasSteamId && (
                            <Chip size="small" label="No Steam" variant="outlined" />
                          )}
                        </Stack>
                      </TableCell>

                      <TableCell align="right">
                        <Tooltip title="More actions">
                          <span>
                            <IconButton
                              size="small"
                              disabled={!u._id}
                              onClick={(e) => openRowMenu(e, u)}
                              aria-label={`Actions for ${u.username}`}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {!loading && visibleItems.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                      <Typography variant="body2" color="text.secondary">
                        No users found. Try a different search or role filter.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={closeRowMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 240 } } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" noWrap>
            {actionUser?.username}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {actionUser?.email || 'No email'}
          </Typography>
        </Box>
        <Divider />
        <MenuItem
          disabled={!actionUser?.steamId}
          onClick={() => openConfirm('resetPassword', actionUser)}
        >
          <ListItemIcon>
            <VpnKeyOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Reset password to Steam ID"
            secondary={
              actionUser?.steamId
                ? 'User must change it on next login'
                : 'Link a Steam ID first'
            }
          />
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => openConfirm('delete', actionUser)} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteOutlineOutlinedIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primary="Delete user" />
        </MenuItem>
      </Menu>

      <Dialog open={confirm.open} onClose={closeConfirm} maxWidth="xs" fullWidth>
        <DialogTitle>{confirmCopy.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmCopy.body}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeConfirm} disabled={confirmBusy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={confirmCopy.color}
            onClick={runConfirmedAction}
            disabled={confirmBusy}
            startIcon={confirmBusy ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {confirmBusy ? 'Working…' : confirmCopy.confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>

      <Feedback />
    </Box>
  );
}
